import { type Database } from '@databases/model/database';
import { type Field, FieldType } from '@databases/model/field';
import { type Value } from '@databases/model/value';
import { CollabLayoutType, type View } from '@databases/model/views/view';
import { CollabFilterType, type CollabFilter } from '@databases/model/views/view-filter';
import { type CollabViewSortType } from '@databases/model/views/view-sort';
import { unparse } from 'papaparse';

import { type Warnings } from './warnings';

function serializeSort(
  collabSort: CollabViewSortType,
  fieldsById: Record<string, Field>
) {
  const fieldName = getFieldName(collabSort.fieldId, fieldsById);
  return `${fieldName} ${collabSort.ascending ? 'ASC' : 'DESC'}`;
}

function getValueForField(value?: Value<FieldType>): string | undefined {
  if (!value) {
    return '';
  }

  const jsonValue = value.toJSON();

  switch (jsonValue.type) {
    case FieldType.Text:
      return `"${jsonValue.value}"`;
  }

  return undefined;
}

function getFieldName(fieldId: string, fieldsById: Record<string, Field>): string {
  const field = fieldsById[fieldId];
  if (!field) {
    throw new Error(`Field ${fieldId} not found`);
  }
  return field.name;
}

function serializeFilter(
  collabFilter: CollabFilter,
  fieldsById: Record<string, Field>,
  wrapInBrackets = true,
  warnings: Warnings
): string | undefined {
  let output = '';

  if (wrapInBrackets) {
    output += '[';
  }

  switch (collabFilter.type) {
    case CollabFilterType.EQUALS: {
      const fieldName = getFieldName(collabFilter.fieldId, fieldsById);
      const value = getValueForField(collabFilter.getValue());

      if (value === undefined) {
        warnings.add(`Value type for field ${collabFilter.fieldId} is unsupported`);
        return undefined;
      }

      const not = collabFilter.not ? 'not ' : '';

      output += `${fieldName} is ${not}${value}`;
      break;
    }
    case CollabFilterType.CONTAINS: {
      const fieldName = getFieldName(collabFilter.fieldId, fieldsById);
      const value = collabFilter.value;

      if (collabFilter.not) {
        output += `${fieldName} does not contain "${value}"`;
      } else {
        output += `${fieldName} contains "${value}"`;
      }
      break;
    }
    case CollabFilterType.EMPTY: {
      const fieldName = getFieldName(collabFilter.fieldId, fieldsById);
      const not = collabFilter.not ? 'not ' : '';

      output += `${fieldName} is ${not}empty`;

      break;
    }
    case CollabFilterType.AND: {
      const filters = collabFilter.value
        .map((e) => serializeFilter(e, fieldsById, false, warnings))
        .filter((e) => e !== undefined)
        .map((e) => `${e}`);

      output += filters.join(', ');
      break;
    }
    case CollabFilterType.OR: {
      const filters = collabFilter.value
        .map((e) => serializeFilter(e, fieldsById, false, warnings))
        .filter((e) => e !== undefined)
        .map((e) => `${e}`);

      output += filters.join('][');
      break;
    }
  }

  if (wrapInBrackets) {
    output += ']';
  }

  return output;
}

function serializeDatabaseView(
  view: View,
  fieldsById: Record<string, Field>,
  warnings: Warnings
) {
  if (view.layoutType === CollabLayoutType.TABLE) {
    const sorts = view.sorts
      .toArray()
      .map((e) => serializeSort(e, fieldsById))
      .join(',');

    const filters = view.filter
      ? serializeFilter(view.filter, fieldsById, true, warnings)
      : undefined;

    return {
      view_name: view.name,
      layout: view.layoutType,
      filters: filters ? `${filters}` : undefined,
      sorts: sorts.length > 0 ? `[${sorts}]` : undefined,
    };
  }
}

/**
 * Serializes the views of a database, including the filters, grouping and sorting.
 *
 * This will serialize as a CSV with the following format:
 *
 * view_name,layout,filters,sorts
 * View 1,table,[Filter 1],[Sort 1]
 * View 2,table,[Filter 2],[Sort 2]
 *
 * @param database The database to serialize the views of.
 * @returns The serialized views.
 */
export function serializeDatabaseViews(
  database: Pick<Database, 'views' | 'viewsById' | 'fieldsById'>,
  warnings: Warnings
): string {
  const databaseViews = database.views
    .map((view) =>
      serializeDatabaseView(database.viewsById[view], database.fieldsById, warnings)
    )
    .filter((view) => view !== undefined);

  if (databaseViews.length === 0) {
    return 'view_name,layout,filters,sorts\r\n';
  }

  return unparse(databaseViews, {
    header: true,
    escapeChar: '\\',
    columns: ['view_name', 'layout', 'filters', 'sorts'],
  });
}
